/**
 * Card shell for one chart block: title, optional hint, optional right-hand slot
 * (tabs), and the mandatory « Graphique » / « Tableau » twin-view toggle when a
 * `table` node is supplied. The table view is the a11y fallback for the SVG, which
 * is why every chart ships one.
 *
 * Style matches the existing `ChartCard` inside ConversationsDashboard.tsx
 * (outlined Paper, radius 2, 1px rgba(0,0,0,0.08) border, uppercase caption title).
 *
 * Every string arrives as a prop (the two toggle labels included) so the container
 * supplies i18n'd copy and this file stays free of `useTranslation`.
 */

import { useState, type ReactNode } from 'react';
import { Box, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { VIZ } from './tokens';

export interface ChartCardProps {
  title: string;
  hint?: ReactNode;
  right?: ReactNode;
  table?: ReactNode;
  /** Labels for the twin-view toggle. Only read when `table` is supplied. */
  chartLabel?: string;
  tableLabel?: string;
  children: ReactNode;
  sx?: object;
}

export default function ChartCard({
  title,
  hint,
  right,
  table,
  chartLabel = 'Graphique',
  tableLabel = 'Tableau',
  children,
  sx,
}: ChartCardProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.68rem',
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 'none' }}>
          {right}
          {table && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => v && setView(v)}
            >
              <ToggleButton value="chart" sx={{ py: 0.3, px: 1, fontSize: '0.72rem', textTransform: 'none' }}>
                {chartLabel}
              </ToggleButton>
              <ToggleButton value="table" sx={{ py: 0.3, px: 1, fontSize: '0.72rem', textTransform: 'none' }}>
                {tableLabel}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>
      {/* Own row, full card width — beside the right-hand slot (up to 7 category tabs
          plus the view toggle) the hint only got the header's first column, so a
          two-clause sentence grew the header by three lines. */}
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
          {hint}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }}>{table && view === 'table' ? table : children}</Box>
    </Paper>
  );
}

/**
 * Legend — mandatory from 2 series, absent for a single one. Identity comes from the
 * coloured swatch; the TEXT stays in the ink tokens (never the series colour).
 */
export function VizLegend({
  items,
  kind = 'rect',
}: {
  items: { label: string; color: string }[];
  kind?: 'rect' | 'line';
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75, mt: 1.25 }}>
      {items.map((it) => (
        <Box
          key={it.label}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: VIZ.ink2 }}
        >
          <Box
            sx={{
              flex: 'none',
              width: kind === 'line' ? 14 : 10,
              height: kind === 'line' ? 2 : 10,
              borderRadius: '2px',
              bgcolor: it.color,
            }}
          />
          <span>{it.label}</span>
        </Box>
      ))}
    </Box>
  );
}

/** "No data" line — a chart with nothing to draw says so, it does not draw zeros. */
export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ color: VIZ.muted, fontSize: 13, py: 1.5 }}>{children}</Typography>
  );
}
